import * as React from 'react';
import styled from 'styled-components';
import { colors } from '../colors/color';
import { Icon } from '../icons/Icons';
import { TextInput, TextInputProps } from './TextInput';

export type TextInputWithSaveProps = Omit<TextInputProps, 'onChange'> & {
    onChange?: (newValue: string) => void,
    onCancel?: (cancelledValue: string, restoredValue: string) => void,
    onSave: (newValue: string) => void,
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void,
    onSaveNoChange?: () => void,
    resetSignal?: unknown,
    placeholder?: string;
};

export function TextInputWithSave (props: TextInputWithSaveProps): JSX.Element
{
    const [thisValue, setThisValue] = React.useState(props.value);
    const [hasFocus, setHasFocus] = React.useState(false);

    const cancelButtonRef = React.useRef(null);
    const saveButtonRef = React.useRef(null);

    React.useEffect(() =>
    {
        setThisValue(props.value);
    }, [props.value, props.resetSignal]);

    function save ()
    {
        // only save if value has changed
        if (thisValue != props.value)
            props.onSave(thisValue);
        else if (props.onSaveNoChange)
            props.onSaveNoChange();

    }

    function handleTextFocus (/*e:React.FocusEvent<HTMLInputElement>*/)
    {
        setHasFocus(true);
    }

    function handleTextBlur (e: React.FocusEvent<HTMLInputElement>)
    {
        const isCancel = e.relatedTarget === cancelButtonRef.current;
        const isSave = e.relatedTarget === saveButtonRef.current;

        setHasFocus(false);

        if (isCancel || isSave)
        {
            // Do nothing yet.
            // it will be handled by the handler
        }
        else
        {
            // any non-button click is implicitly saving
            save();
        }
    }

    function handleCancelClick ()
    {
        if (thisValue != props.value)
        {
            setThisValue(props.value);
        }
        if (props.onCancel)
            props.onCancel(thisValue, props.value);
    }

    function handleSaveClick ()
    {
        save();
    }

    function handleChange (newValue: string)
    {
        setThisValue(newValue);
        if (props.onChange)
            props.onChange(newValue);
    }

    function handleKeyDown (e: React.KeyboardEvent<HTMLInputElement>)
    {
        // eslint-disable-next-line no-empty
        if (e.key === 'Tab')
        {
        }
        if (e.key === 'Enter')
        {
            save();
        }
        props.onKeyDown?.(e);
    }

    return (
        <TextWithSaveContainer className="textInputWithSaveContainer">

            <TextInput {...props}
                value={thisValue}
                placeholder={props.placeholder}
                onChange={handleChange}
                onFocus={handleTextFocus}
                onBlur={handleTextBlur}
                onKeyDown={handleKeyDown} />

            <TextWithSaveButtonRow style={{ visibility: hasFocus ? "visible" : "hidden" }}>

                <TextWithSaveCancelButton
                    ref={cancelButtonRef}
                    tabIndex={1}
                    onClick={handleCancelClick}>
                    <Icon name={"actions_close"} size={"small"} />
                </TextWithSaveCancelButton>

                <TextWithSaveSaveButton
                    ref={saveButtonRef}
                    tabIndex={2}
                    onClick={handleSaveClick}>
                    <Icon name={"actions_done"} size={"small"} />
                </TextWithSaveSaveButton>

            </TextWithSaveButtonRow>

        </TextWithSaveContainer>
    );
}

export const TextWithSaveContainer = styled.div`
    position: relative;
`;

export const TextWithSaveButtonRow = styled.div`
    
    position:absolute;
    z-index: 1000;
    display: 'block';
    margin-top: 2px;
    width: 100%;
    text-align: right;
    pointer-events: none;
    & > button {
        margin-left:5px;
        pointer-events: auto;
    }
`;

export const TextWithSaveCancelButton = styled.button`
    border: 1px solid ${colors.withSaveButton.cancel.border};
    border-radius: 4px;
    padding:2px;
    background-color: ${colors.withSaveButton.cancel.background};
    & svg {
        fill:${colors.withSaveButton.cancel.color};
    }

    &:hover{
        border-color: ${colors.withSaveButton.cancel.borderHover};
        cursor: pointer;
        & svg {
            fill:${colors.withSaveButton.cancel.colorHover};
        }
     
    }
`;

export const TextWithSaveSaveButton = styled.button`
    border: 1px solid ${colors.withSaveButton.save.border};
    border-radius: 4px;
    padding:2px;
    background-color: ${colors.withSaveButton.save.background};
    
    & svg {
        fill:${colors.withSaveButton.save.color};
    }

    &:hover{
        border-color: ${colors.withSaveButton.save.borderHover};
        cursor: pointer;
        & svg {
            fill:${colors.withSaveButton.save.colorHover};
        }
    }
`;